/** Business rules shared by API and web. Source of truth: docs/PRD.md §7. */

/** Version of Terms/Privacy/Disclaimer = date of last update. Changing it re-prompts every user (source `terms_update`). */
export const TERMS_VERSION = '2026-10-21'

export const TZ = 'Asia/Jakarta'

/** CloudPass key: XXXX-XXXX-XXXX-XXXX, alphabet without 0/O/1/I (Integration-Guide §2). */
export const KEY_RE = /^[A-HJ-NP-Z2-9]{4}(-[A-HJ-NP-Z2-9]{4}){3}$/

/** Points ("cahaya") per activity → garden level. */
export const LIGHT_POINTS = {
  habit: 1,
  niat_done: 2,
  muhasabah: 2,
  challenge_day: 3,
  ayat: 2,
  share: 1,
  mutiara_full: 2,
  ulang_benar: 1,
} as const
export type LightKind = keyof typeof LIGHT_POINTS

/** Cumulative points needed for garden level 1..5. */
export const LEVEL_THRESHOLDS = [0, 20, 60, 120, 220] as const

/** Spaced repetition gaps in days, indexed by level 0..4. */
export const ULANG_INTERVALS_DAYS = [1, 3, 7, 14, 30] as const

/** Onboarding (docs/PRD.md US-2.1–2.4). `maknai`: reads Arabic fluently → straight to meaning; `baca`: still learning. */
export const JALUR = ['maknai', 'baca'] as const
export type Jalur = (typeof JALUR)[number]
/** Reminder minutes are multiples of 15 (the push scheduler runs every 15 minutes). */
export const REMINDER_MINUTES = [0, 15, 30, 45] as const
export const DEFAULT_REMINDER = { enabled: true, hour: 5, minute: 0 } as const
/** "Rencana" answers: where the user struggles (≤ 2), who they want to become, minutes per day. */
export const PLAN_BERJUANG = ['amarah', 'hp', 'shalat', 'uang', 'anak', 'cemas', 'baca'] as const
export const PLAN_BERJUANG_MAX = 2
export const PLAN_JADI = ['tenang', 'sabar', 'syukur', 'dekat', 'bermanfaat'] as const
export const PLAN_MENIT = [5, 10, 20] as const

/** The four pillars of QS. Al-‘Asr 103:1–3 (iman, amal saleh, kebenaran, kesabaran): how Perjalanan groups activities. */
export const PILAR = ['iman', 'amal', 'kebenaran', 'sabar'] as const
export type Pilar = (typeof PILAR)[number]
export const PILAR_LABEL: Record<Pilar, string> = { iman: 'Iman', amal: 'Amal saleh', kebenaran: 'Kebenaran', sabar: 'Kesabaran' }

/**
 * Habit presets (docs/PRD.md §6.12). Names of everyday practices only, no claims about reward; `key` = habit_key.
 * The default active set equals the database default of habit_config.active_keys.
 */
export const HABIT_PRESETS = [
  { key: 'shalat', label: 'Shalat tepat waktu', pilar: 'amal' },
  { key: 'tilawah', label: 'Membaca Al-Qur’an, walau satu ayat', pilar: 'iman' },
  { key: 'zikir', label: 'Zikir pagi dan petang', pilar: 'iman' },
  { key: 'sedekah', label: 'Sedekah, walau sedikit', pilar: 'amal' },
  { key: 'nasihat', label: 'Mengajak atau mengingatkan satu kebaikan', pilar: 'kebenaran' },
  { key: 'lisan', label: 'Menjaga lisan: jujur, tidak menggunjing', pilar: 'kebenaran' },
  { key: 'marah', label: 'Menahan marah', pilar: 'sabar' },
  { key: 'layar', label: 'Membatasi waktu di layar', pilar: 'sabar' },
] as const satisfies readonly { key: string; label: string; pilar: Pilar }[]
export const HABIT_DEFAULT = ['shalat', 'tilawah', 'zikir', 'sedekah'] as const
export const HABIT_ACTIVE_MIN = 3
export const HABIT_ACTIVE_MAX = 5
export const HABIT_CUSTOM_MAX = 5
/** "Rencana" answers → the habit preset that fits (recommendations, Perjalanan → Rencana). */
export const PLAN_HABIT: Record<string, string> = {
  amarah: 'marah', hp: 'layar', shalat: 'shalat', uang: 'sedekah', anak: 'nasihat', cemas: 'zikir', baca: 'tilawah',
  tenang: 'zikir', sabar: 'marah', syukur: 'sedekah', dekat: 'tilawah', bermanfaat: 'nasihat',
}

export const NIAT_MAX_CHARS = 160
export const NIAT_OPEN_MAX = 30
export const NIAT_SOURCES = ['pegangan', 'tantangan', 'mutiara', 'ayat', 'sendiri'] as const
export type NiatSource = (typeof NIAT_SOURCES)[number]

/** Muhasabah: "did today's small step happen?" */
export const MUHASABAH_JALAN = ['ya', 'sebagian', 'belum'] as const
export const MUHASABAH_MAX_CHARS = 200
export const MUHASABAH_REMINDER = { hour: 20, minute: 30 } as const

export const CHALLENGE_DAYS = 7
export const REFLECTION_MAX_CHARS = 300

/**
 * Kompas Diri (docs/DATA-MODEL.md §4.6): activity of the last 30 days per pillar against these targets → 0–100, then a
 * stage. A mirror of activity in the app, never a measure of faith.
 */
export const KOMPAS_DAYS = 30
export const KOMPAS_TARGETS: Record<Pilar, number> = { iman: 40, amal: 50, kebenaran: 30, sabar: 40 }
export const KOMPAS_STAGES = [
  { min: 0, label: 'Benih' },
  { min: 15, label: 'Tunas' },
  { min: 40, label: 'Tumbuh' },
  { min: 75, label: 'Rimbun' },
] as const

/** Pegangan feelings (docs/PRD.md US-6.1), in display order. */
export const RASA = ['kesal', 'cemas', 'sedih', 'capek', 'gelisah', 'jauh', 'syukur'] as const
export type Rasa = (typeof RASA)[number]

export const MUTIARA_DAILY_LIMIT = 5
/**
 * Mutiara card kinds (docs/PRD.md US-10.1). Every kind is built from the verified datasets, never written by AI:
 * ayat = verse + Tafsir Ringkas Kemenag, hadis = Sahih hadith from HadeethEnc (verbatim), kata = one Qur'anic word
 * (Quran.com word by word), pegangan = a Pegangan card that passed the grounding evaluation.
 */
export const MUTIARA_JENIS = ['ayat', 'hadis', 'kata', 'pegangan'] as const
export type MutiaraJenis = (typeof MUTIARA_JENIS)[number]

/** In-app support form (docs/PRD.md M12): category → label in the ticket subject and the form. */
export const SUPPORT_CATEGORIES = {
  akun: 'Akun & lisensi',
  bayar: 'Pembayaran',
  konten: 'Isi ayat & konten',
  teknis: 'Masalah teknis',
  saran: 'Saran',
  lainnya: 'Lainnya',
} as const
export type SupportCategory = keyof typeof SUPPORT_CATEGORIES
/** Tickets per user per hour (counted only when mail is configured). */
export const SUPPORT_PER_HOUR = 5

export const CERITA_FREE_QUOTA = 5
export const CERITA_MAX_CHARS = 1200

/**
 * Crisis contacts for Teman Cerita replies of kind "darurat" (docs/PRD.md §9.4: verified, never from memory).
 * Checked 2026-10-22; re-check before every release. `url` = the service's own site, `source` = where the number was
 * verified.
 */
export const DARURAT_CONTACTS = [
  {
    name: 'Healing119, layanan kesehatan jiwa Kemenkes',
    call: '119',
    how: 'Telepon 119, lalu pilih ekstensi 8',
    url: 'https://healing119.id',
    source: 'https://health.detik.com/berita-detikhealth/d-8696859/kemenkes-fitur-chat-healing-119-lagi-perbaikan-antrean-layanan-kesehatan-mental-melonjak',
  },
  {
    name: 'Panggilan darurat 112 (gratis, bisa saat ponsel terkunci)',
    call: '112',
    how: 'Telepon 112 untuk keadaan darurat: ancaman keselamatan, kekerasan, atau butuh pertolongan medis segera',
    /** no single official page for 112 */
    url: null,
    source: 'https://jatim.antaranews.com/berita/911205/pemkot-kediri-satukan-layanan-di-call-center-lapor-mbak-wali-112',
  },
] as const
export const DARURAT_CHECKED = '2026-10-22'

/** Local anti-sharing cap (CloudPass device policy is recommended OFF). */
export const MAX_ACTIVE_SESSIONS = 3

const HOUR = 3_600_000
/** Re-check the license with CloudPass when older than this (+ per-link jitter). */
export const REVALIDATE_AFTER_MS = 24 * HOUR
export const REVALIDATE_JITTER_MS = 6 * HOUR
/** If CloudPass is unreachable, an existing session keeps working this long after the last successful validation. */
export const OFFLINE_GRACE_MS = 7 * 24 * HOUR

export const SESSION_TTL_DAYS = 180
export const SESSION_COOKIE = 'iqp_sess'
export const DEVICE_COOKIE = 'iqp_did'
/** 400 days = the maximum browsers honour (RFC 6265bis). Refreshed on every login. */
export const DEVICE_COOKIE_MAX_AGE_S = 400 * 24 * 60 * 60
/** Never change after release: it namespaces deviceId per app (Integration-Guide §18.6). */
export const DEVICE_ID_PREFIX = 'iqrapath:'

export const GENERIC_INVALID_MESSAGE = 'License key atau email tidak valid. Cek kembali atau hubungi admin.'

/** Attribution for the Qur'an data (data/quran/README.md). Shown next to the text and on the settings page. */
export const QURAN_CREDIT = 'Teks, terjemah & tafsir: Qur’an Kemenag, LPMQ Kemenag RI'
/** Shown wherever word-by-word meanings or word audio appear. */
export const WORDS_CREDIT = 'Arti & suara per kata: Quran.com'
/** Shown with the juz list (data/quran/juz.json). */
export const JUZ_CREDIT = 'Pembagian juz: Quran.com'
